import React, { useEffect, useRef, useState } from 'react'
import { FaMagnifyingGlass } from "react-icons/fa6";
import { RiArrowDownSLine } from "react-icons/ri";
import WRGGHomeLogo from './WRGGHomeLogo';
import BetaBadge from './BetaBadge';
import Seo from './Seo';
import SearchSuggestionDropdown from './search/SearchSuggestionDropdown';
import SearchValidationToast from './search/SearchValidationToast';
import { scopedPlatformOptions, useDeploymentCapabilities } from '../context/DeploymentCapabilitiesContext';

const PLATFORM_OPTIONS = [
    { value: "", label: "--", disabled: true },
    { value: "na1", label: "NA" },
    { value: "euw1", label: "EUW" },
    { value: "br1", label: "BR" },
    { value: "eun1", label: "EUNE" },
    { value: "kr", label: "KR" },
    { value: "oc1", label: "OCE" },
    { value: "la1", label: "LAN" },
    { value: "la2", label: "LAS" },
    { value: "ru", label: "RU" },
    { value: "tr1", label: "TR" },
    { value: "me1", label: "ME" },
    { value: "jp1", label: "JP" },
    { value: "sg2", label: "SG" },
    { value: "tw2", label: "TW" },
    { value: "vn2", label: "VN" },
]

const Home = ({
    formErrors,
    setFormErrors,
    platform,
    handleSummonerNameEntry,
    handlePlatformSelection,
    submitAndSearchSummoner,
    summonerSearchFormData,
    championSuggestions,
    recentSearches,
    searchSuggestions,
    searchSuggestionsLoading,
    onSelectChampionAnalytics,
    onSelectChampionPlayers,
    onSelectRecentSearch,
    onSelectSearchSuggestion,
    onSubmitDirectSearch,
    onViewAllSearchResults,
}) => {
    const deploymentCapabilities = useDeploymentCapabilities()
    const scopedOptions = scopedPlatformOptions(PLATFORM_OPTIONS, deploymentCapabilities)
    const selectedPlatform = platform || ""
    const showTaglineHint = !summonerSearchFormData?.gameName && !summonerSearchFormData?.tagLine
    const [isCompactSearch, setIsCompactSearch] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth < 420 : false
    )
    const [showSuggestions, setShowSuggestions] = useState(false)
    const searchContainerRef = useRef(null)

    useEffect(() => {
        if (typeof window === "undefined") return undefined

        const mediaQuery = window.matchMedia("(max-width: 419px)")
        const syncCompactSearch = (event) => setIsCompactSearch(event.matches)

        setIsCompactSearch(mediaQuery.matches)

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", syncCompactSearch)
            return () => mediaQuery.removeEventListener("change", syncCompactSearch)
        }

        mediaQuery.addListener(syncCompactSearch)
        return () => mediaQuery.removeListener(syncCompactSearch)
    }, [])

    useEffect(() => {
        if (!showSuggestions) {
            return undefined
        }

        function handlePointerDown(event) {
            const clickedInsideDropdown = event.target instanceof Element
                && Boolean(event.target.closest('[data-search-suggestion-dropdown="true"]'))

            if (!searchContainerRef.current?.contains(event.target) && !clickedInsideDropdown) {
                setShowSuggestions(false)
            }
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        return () => document.removeEventListener('pointerdown', handlePointerDown, true)
    }, [showSuggestions])

    return (
        <div className="mx-auto flex w-full max-w-screen-lg flex-col items-center px-4 pt-11 pb-24">
            <Seo
                title="WINRATES.GG™ | The unified League of Legends® stats companion app — For Winners"
                description="WINRATES.GG is the unified League of Legends® companion app, bringing player profiles, champion analytics, the best item builds and rune builds, ranked tier lists, match history, live games, mastery leaderboards, and detailed player and champion stats together in one place. Get advanced insights for free, including champion scaling profiles, Red Kayn vs. Blue Kayn stats and best Kayn form analysis, champion evolution stats and best evolution order, first item and rush item completion timing, jungle and support item completion times, quest stats, and True Lead—an objective measure of how your team is actually performing."
                canonical="/"
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: 'WINRATES.GG',
                    url: 'https://winrates.gg/',
                    description: 'WINRATES.GG is the unified League of Legends® companion app, bringing player profiles, champion analytics, the best item builds and rune builds, ranked tier lists, match history, live games, mastery leaderboards, and detailed player and champion stats together in one place. Get advanced insights for free, including champion scaling profiles, Red Kayn vs. Blue Kayn stats and best Kayn form analysis, champion evolution stats and best evolution order, first item and rush item completion timing, jungle and support item completion times, quest stats, and True Lead—an objective measure of how your team is actually performing.',
                }}
            />
            <div className="flex w-full flex-col items-center justify-center text-center leading-none">
                <div className="mx-auto flex w-fit items-center justify-center gap-0.5 sm:gap-1.5">
                    <WRGGHomeLogo className="block h-24 w-auto shrink-0" />
                    <BetaBadge className="hidden shrink-0 sm:inline-flex" />
                </div>
                <div className="mt-1 text-center">
                    <h1 className="text-[11px] font-extralight leading-5 tracking-[0.09em] text-zinc-300 sm:text-[13px] sm:leading-normal">
                        The unified League of Legends® stats<span className="block sm:inline"><span className="hidden sm:inline">{' '}</span>companion app — For Winners.</span>
                    </h1>
                </div>
            </div>

            <div className="mt-16 w-full max-w-[820px] min-w-[300px]">
                <div className='px-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-300'>Select a region</div>
                <form onSubmit={(e) => {
                    setShowSuggestions(false)
                    submitAndSearchSummoner(e)
                }}>
                    <div ref={searchContainerRef} className="relative z-[1400]">
                    <div className="group flex min-h-[42px] w-full items-center rounded-full border border-zinc-700/85 bg-zinc-900/72 pr-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition duration-200 hover:border-zinc-600 focus-within:border-cyan-700/80 focus-within:shadow-[0_0_0_1px_rgba(8,145,178,0.24)]">
                        <div className="relative flex h-full w-[68px] shrink-0 items-center pl-1 min-[420px]:w-[76px] min-[420px]:pl-1.5">
                            <select
                                aria-label="Region"
                                className="h-8 w-full appearance-none rounded-full border border-transparent bg-transparent pl-2.5 pr-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-200 transition duration-200 hover:text-zinc-100 focus:outline-none min-[420px]:pl-3 min-[420px]:pr-6 min-[420px]:tracking-[0.2em]"
                                value={selectedPlatform}
                                onChange={(e) => handlePlatformSelection(e)}
                                name="platform"
                            >
                                {scopedOptions.map((option) => (
                                    <option key={option.value || "placeholder"} value={option.value} disabled={option.disabled}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <RiArrowDownSLine className="pointer-events-none absolute right-1.5 text-[15px] text-zinc-300 transition duration-200 group-focus-within:text-cyan-300 min-[420px]:right-2.5 min-[420px]:text-base" />
                        </div>

                        <div className="mx-2 h-4 w-px shrink-0 bg-zinc-700/90" />

                        <div className="relative flex min-w-0 flex-1 items-center">
                            <input
                                autoComplete='off'
                                autoCorrect='off'
                                spellCheck='false'
                                aria-label="Search summoner"
                                className="min-w-0 w-full bg-transparent px-1 text-[13px] text-zinc-200 placeholder:text-[12px] placeholder:font-normal placeholder:italic placeholder:text-zinc-400 focus:outline-none min-[420px]:text-[15px] min-[420px]:placeholder:text-[13px]"
                                onChange={handleSummonerNameEntry}
                                onInput={handleSummonerNameEntry}
                                onFocus={() => setShowSuggestions(true)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        setShowSuggestions(false)
                                    }
                                }}
                                placeholder={isCompactSearch ? "Summoner  +" : "Search Summoner  +"}
                                name="gameName"
                                type="text"
                                value={summonerSearchFormData?.query || ''}
                            />
                            {showTaglineHint ? (
                                <span className="pointer-events-none absolute left-[82px] top-1/2 -translate-y-1/2 rounded-md border border-cyan-700/30 bg-cyan-900/15 px-2 py-[3px] text-[11px] font-normal tracking-[0.03em] text-cyan-100/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] min-[420px]:left-[134px]">
                                    #TagLine
                                </span>
                            ) : null}
                        </div>

                        <button
                            aria-label="Search"
                            className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-300 transition duration-200 hover:bg-zinc-800/80 hover:text-zinc-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70"
                            type="submit"
                        >
                            <FaMagnifyingGlass className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    {showSuggestions ? (
                        <SearchSuggestionDropdown
                            anchorRef={searchContainerRef}
                            championResults={championSuggestions}
                            hasSelectedRegion={Boolean(selectedPlatform)}
                            loading={searchSuggestionsLoading}
                            query={summonerSearchFormData?.query || ''}
                            recentSearches={recentSearches}
                            results={searchSuggestions}
                            onSelectChampionAnalytics={(champion) => {
                                setShowSuggestions(false)
                                onSelectChampionAnalytics(champion)
                            }}
                            onSelectChampionPlayers={(champion) => {
                                setShowSuggestions(false)
                                onSelectChampionPlayers(champion)
                            }}
                            onSelectRecentSearch={(recentSearch) => {
                                setShowSuggestions(false)
                                onSelectRecentSearch(recentSearch)
                            }}
                            onSelectSummoner={(result) => {
                                setShowSuggestions(false)
                                onSelectSearchSuggestion(result)
                            }}
                            onSubmitDirectSearch={() => {
                                setShowSuggestions(false)
                                onSubmitDirectSearch()
                            }}
                            onViewAllResults={() => {
                                if (!(summonerSearchFormData?.query || '').trim()) {
                                    return
                                }
                                setShowSuggestions(false)
                                onViewAllSearchResults()
                            }}
                        />
                    ) : null}
                    {formErrors?.length > 0 ? (
                        <SearchValidationToast
                            messages={formErrors}
                            onDismiss={() => setFormErrors([])}
                        />
                    ) : null}
                    </div>
                </form>
                <div className='mt-3 px-2 text-left text-[11px] tracking-[0.03em] text-zinc-400'>
                    <div>
                        Try searching for a summoner profile. Ex:
                    </div>
                    <div className='mt-1 text-zinc-400'>
                        [NA] - Doublelift#NA01
                    </div>
                </div>
            </div>

            <section
                aria-label="What WINRATES.GG brings together"
                className="mt-14 w-full max-w-[820px] border-y border-zinc-800/90 py-4 sm:mt-16"
            >
                <ul className="grid grid-cols-1 divide-y divide-zinc-800/80 sm:grid-cols-4 sm:divide-x sm:divide-y-0">
                    <li className="px-2 py-3 text-center sm:py-1 sm:text-left">
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Definitive</span>
                        <span className="mt-1 block text-sm font-medium tracking-[0.01em] text-zinc-100">Tier Lists</span>
                    </li>
                    <li className="px-2 py-3 text-center sm:py-1 sm:text-left sm:pl-5">
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Definitive</span>
                        <span className="mt-1 block text-sm font-medium tracking-[0.01em] text-zinc-100">Player Profiles</span>
                    </li>
                    <li className="px-2 py-3 text-center sm:py-1 sm:text-left sm:pl-5">
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Best</span>
                        <span className="mt-1 block text-sm font-medium tracking-[0.01em] text-zinc-100">Champion &amp; Build Stats</span>
                    </li>
                    <li className="px-2 py-3 text-center sm:py-1 sm:text-left sm:pl-5">
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Only</span>
                        <span className="mt-1 block text-sm font-medium tracking-[0.01em] text-zinc-100">Unique Advanced Analytics</span>
                    </li>
                </ul>
            </section>
        </div>
    )
}

export default Home
